import { useState, useMemo } from "react";
import { LayoutDashboard, Package } from "lucide-react";
import Overview from "../overview";
import OfferDashboard from "./OfferDashboard";
import AgencyPnl from "./AgencyPnl";
import AgencySummary from "./AgencySummary";
import OwnerKpiCard from "./OwnerKpiCard";
import ClientBudgetCard from "./ClientBudgetCard";
import PartnerOwedCard from "./PartnerOwedCard";

// Dashboard tab is a per-offer P&L view. The "All offers" sub-tab shows
// the existing global Overview (MRR/ARR/churn/etc). Every offer gets its
// own sub-tab automatically — sorted with recurring offers first, then
// one-offs, alpha within each group.
export default function Dashboard({ state, dashboard, dateFilter, onOpenClient, onJumpToDept }) {
  const offers = state.offers || [];

  const sortedOffers = useMemo(() => {
    const recurring = offers.filter((o) => o.type === "recurring");
    const oneoff = offers.filter((o) => o.type === "oneoff");
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
    return [...recurring.sort(byName), ...oneoff.sort(byName)];
  }, [offers]);

  const [active, setActive] = useState("all");

  // If the active offer was deleted, fall back to "all"
  const isValidActive = active === "all" || sortedOffers.some((o) => o.id === active);
  const currentActive = isValidActive ? active : "all";

  const activeOffer = sortedOffers.find((o) => o.id === currentActive);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 border-b border-gray-100 dark:border-[var(--border-soft)] overflow-x-auto">
        <SubTabButton
          icon={LayoutDashboard}
          label="All offers"
          active={currentActive === "all"}
          onClick={() => setActive("all")}
        />
        {sortedOffers.map((o) => (
          <SubTabButton
            key={o.id}
            icon={Package}
            label={o.name}
            active={currentActive === o.id}
            onClick={() => setActive(o.id)}
          />
        ))}
      </div>

      {currentActive === "all" || !activeOffer ? (
        <div className="space-y-6">
          <AgencySummary state={state} dateFilter={dateFilter} onJumpToDept={onJumpToDept} />
          <div className="border-t border-gray-100 dark:border-[var(--border-soft)] pt-4 space-y-6">
            <OwnerKpiCard state={state} />
            <PartnerOwedCard state={state} />
            <ClientBudgetCard state={state} />
            <AgencyPnl state={state} />
          </div>
          <div className="border-t border-gray-100 dark:border-[var(--border-soft)] pt-4">
            <div className="text-[11px] uppercase tracking-wide text-brand-slate mb-3">
              Operating overview (clients, MRR, churn)
            </div>
            <Overview state={state} dashboard={dashboard} />
          </div>
        </div>
      ) : (
        <OfferDashboard offer={activeOffer} state={state} onOpenClient={onOpenClient} />
      )}
    </div>
  );
}

function SubTabButton({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
        active
          ? "border-brand-blue text-brand-ink dark:text-[var(--text-primary)]"
          : "border-transparent text-brand-slate hover:text-brand-ink"
      }`}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}